import { useEffect, useReducer, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import * as answerService from "@/api/studentSimulationManagementService";
import { simulationService } from "@/api/simulationService";
import { parseApiError } from "@/utils/errorHandler";
import {
    answersReducer,
    createInitialState,
} from "./studentSimulationManagementReducer";

const ANSWERS_PER_PAGE = 15;

export const useStudentSimulationManagement = () => {
    const { userId, simulationId } = useParams<{
        userId: string;
        simulationId: string;
    }>();
    const [state, dispatch] = useReducer(answersReducer, createInitialState());

    useEffect(() => {
        const fetchSimulationStructure = async () => {
            if (!simulationId) return;

            dispatch({ type: "FETCH_START" });
            try {
                const simulation = await simulationService.getById(simulationId);
                dispatch({
                    type: "SET_SIMULATION_DATA",
                    payload: {
                        ...simulation,
                        sessions: simulation.sessions || [],
                        state: simulation.state || "unknown",
                    },
                });

                const firstSessionId = simulation.sessions?.[0]?.id ?? null;
                const firstTestId = simulation.sessions?.[0]?.tests?.[0]?.id ?? null;
                dispatch({
                    type: "SET_FILTERS",
                    payload: { sessionId: firstSessionId, testId: firstTestId },
                });
            } catch (err) {
                dispatch({ type: "FETCH_FAILURE", payload: parseApiError(err) });
            }
        };

        fetchSimulationStructure();
    }, [simulationId]);

    useEffect(() => {
        const fetchPaginatedQuestions = async () => {
            if (
                !userId ||
                !simulationId ||
                !state.filters.sessionId ||
                !state.filters.testId
            ) {
                if (state.isLoading) dispatch({ type: "FETCH_SUCCESS" });
                return;
            }

            dispatch({ type: "FETCH_START" });
            try {
                const filters = {
                    page: state.currentPage,
                    page_size: ANSWERS_PER_PAGE,
                    session_id: state.filters.sessionId,
                    test_id: state.filters.testId,
                };
                const data = await answerService.getAnswers(
                    userId,
                    simulationId,
                    filters,
                );
                dispatch({ type: "SET_PAGINATED_QUESTIONS", payload: data });
            } catch (err) {
                dispatch({ type: "FETCH_FAILURE", payload: parseApiError(err) });
            }
        };

        fetchPaginatedQuestions();
    }, [userId, simulationId, state.currentPage, state.filters]);

    const availableTests = useMemo(() => {
        if (!state.filters.sessionId) return [];
        const session = state.simulationData.sessions.find(
            (s) => s.id === state.filters.sessionId,
        );
        return session ? session.tests : [];
    }, [state.simulationData.sessions, state.filters.sessionId]);

    const handleSessionChange = useCallback(
        (sessionId: number) => {
            const newSession = state.simulationData.sessions.find(
                (s) => s.id === sessionId,
            );
            const firstTestId = newSession?.tests?.[0]?.id ?? null;
            dispatch({
                type: "SET_FILTERS",
                payload: { sessionId, testId: firstTestId },
            });
        },
        [state.simulationData.sessions],
    );

    const handleTestChange = useCallback((testId: number) => {
        dispatch({ type: "SET_FILTERS", payload: { testId } });
    }, []);

    const handleAnswerChange = useCallback(
        (questionId: number, newAnswer: string) => {
            dispatch({
                type: "UPDATE_ONE_ANSWER",
                payload: { questionId, answer: newAnswer },
            });
        },
        [],
    );

    const setCurrentPage = useCallback((page: number) => {
        dispatch({ type: "SET_PAGE", payload: page });
    }, []);

    const handleSave = useCallback(async () => {
        if (!userId || !simulationId) return;
        dispatch({ type: "SAVE_START" });
        try {
            await answerService.updateStudentAnswers(
                userId,
                simulationId,
                state.answers,
            );
            dispatch({ type: "SAVE_SUCCESS" });
        } catch (err) {
            const parsedError = parseApiError(err);
            dispatch({ type: "SAVE_FAILURE", payload: parsedError });
            throw parsedError;
        }
    }, [userId, simulationId, state.answers]);

    return {
        state,
        availableTests,
        paginatedQuestions: state.questions,
        totalQuestions: state.count,
        pageSize: ANSWERS_PER_PAGE,
        handleSessionChange,
        handleTestChange,
        handleAnswerChange,
        handleSave,
        setCurrentPage,
    };
};
